import Cookies from "js-cookie";
import * as React from "react";
import { Constants } from "@socialcoach/services";
import { PropsWithChildren, useState, useEffect, useCallback, useMemo } from "react";
import { SessionServices, SocialCoachSession } from "@/services/bedrock";
import { clearUserStorage } from "@/utils";

type State = {
  session?: SocialCoachSession;
  clearSession: (isExplicitLogout?: boolean) => void;
  refreshSession: (background?: boolean) => Promise<SocialCoachSession | undefined>;
  loading: boolean;
};

export const SessionContext = React.createContext<State>({
  loading: false,
  clearSession: (isExplicitLogout?: boolean) => null,
  refreshSession: async () => {
    /* default */
    return undefined;
  },
  session: undefined,
});

type Props = {
  session?: SocialCoachSession;
  loading?: boolean;
};

export const SessionContextProvider: React.FC<PropsWithChildren<Props>> = ({
  children,
  session: initialSession,
  loading: initialLoading,
}) => {
  const [session, setSession] = useState<SocialCoachSession | undefined>(initialSession);
  const [loading, setLoading] = useState<boolean>(initialLoading === undefined ? true : initialLoading);

  const clearSession = useCallback(
    (isExplicitLogout = false): void => {
      if (session?.playerId) {
        clearUserStorage(session.playerId, isExplicitLogout);
      } else {
        // Sweeps generic keys if no specific playerId is available
        clearUserStorage(undefined, isExplicitLogout);
      }
      Cookies.remove(Constants.COOKIE_NAME);
      setSession(undefined);
    },
    [session]
  );

  const checkForSession = useCallback(async (): Promise<SocialCoachSession | undefined> => {
    try {
      const returnedSession = await SessionServices.getMe(true);
      setSession(returnedSession);
      return returnedSession;
    } catch (e: any) {
      setSession(undefined);
      console.warn("Error getting session data: ", e);
    } finally {
      setLoading(false);
    }
    return undefined;
  }, []);

  const refreshSession = useCallback(
    async (background = false): Promise<SocialCoachSession | undefined> => {
      if (!background) {
        setLoading(true);
      }
      return await checkForSession();
    },
    [checkForSession]
  );

  useEffect(() => {
    checkForSession().catch(console.error);
  }, [checkForSession]);

  const contextValue = useMemo(
    () => ({
      session,
      clearSession,
      refreshSession,
      loading,
    }),
    [clearSession, refreshSession, loading, session]
  );

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};
